import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack } from '@mui/material';
import { AddRounded, EditRounded, DeleteRounded } from '@mui/icons-material';
import { useCustomers } from '@/queries/customers';
import AdminPanelPage from '@/views/layouts/admin-panel/AdminPanelPage';
import Backdrop from '@/components/ui/backdrop';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import CustomerForm, { CustomerFormModel } from '@/features/customers/CustomerForm';
import DeleteCustomerForm from '@/features/customers/DeleteCustomerForm';
import { initialCustomerFormModel } from '@/features/customers/customer-form.defaults';
import { Customer } from '@/core/entities';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting } from 'devextreme-react/data-grid';

const CustomersPage: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [customers, setCustomers] = useState<Customer[]>([]);

    const {
        queryKey: useCustomersQueryKey,
        query: { data: customerPages, isLoading },
    } = useCustomers({
        queryParams: { limit: 99999 },
    });

    useEffect(() => {
        if (customerPages) {
            setCustomers(customerPages.pages[0].data);
        }
    }, [customerPages]);

    const [customerForm, setCustomerForm] = useState<{
        open: boolean;
        formModel: CustomerFormModel;
        customerId?: string;
    }>({
        open: false,
        formModel: initialCustomerFormModel,
    });

    const refetch = () => {
        /* eslint-disable @typescript-eslint/no-floating-promises */
        queryClient.invalidateQueries(useCustomersQueryKey);
    };

    const [deleteCustomerForm, setDeleteCustomerForm] = useState<{ open: boolean; model?: Customer }>(
        { open: false }
    );

    const handleEdit = (customer: Customer) => {
        setCustomerForm({
            customerId: customer.id,
            formModel: { ...customer },
            open: true,
        });
    };

    const handleDelete = (customer: Customer) => {
        setDeleteCustomerForm({ model: customer, open: true });
    };

    const renderActionsCell = (cellData: { data: Customer }) => {
        const customer = cellData.data;
        return (
            <Stack direction="row" spacing={1}>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(customer);
                    }}
                >
                    <EditRounded fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(customer);
                    }}
                >
                    <DeleteRounded fontSize="small" />
                </IconButton>
            </Stack>
        );
    };

    return (
        <AdminPanelPage
            title="Customer Management"
            headerControls={
                <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    size="large"
                    onClick={() =>
                        setCustomerForm({
                            customerId: undefined,
                            formModel: initialCustomerFormModel,
                            open: true,
                        })
                    }
                >
                    Create Customer
                </Button>
            }
        >
            {isLoading ? (
                <Backdrop />
            ) : (
                <Box component={Paper} p={3}>
                    <Stack direction="column" spacing={3}>
                        <DataGrid
                            dataSource={customers}
                            showBorders={true}
                            columnAutoWidth={true}
                            rowAlternationEnabled={false}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                            hoverStateEnabled={true}
                            onRowClick={(e) => {
                                if (e.data) {
                                    navigate(`/customers/${(e.data as Customer).id}`);
                                }
                            }}
                        >
                            <Sorting mode="multiple" />
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />
                            <SearchPanel visible={true} highlightCaseSensitive={true} />
                            <Paging defaultPageSize={50} enabled={true} />

                            <Column dataField="name" caption="Customer" />
                            <Column dataField="numberOfSites" caption="Number of sites" alignment="right" />
                            <Column dataField="numberOfDevices" caption="Number of devices" alignment="right" />
                            <Column
                                caption="Actions"
                                width={120}
                                allowFiltering={false}
                                allowSorting={false}
                                allowReordering={false}
                                cellRender={renderActionsCell}
                            />
                        </DataGrid>
                    </Stack>
                </Box>
            )}
            <CustomerForm
                open={customerForm.open}
                onClose={() => setCustomerForm({ ...customerForm, open: false })}
                formModel={customerForm.formModel}
                updateFormModel={(field: keyof CustomerFormModel, value: string) =>
                    setCustomerForm({
                        ...customerForm,
                        formModel: { ...customerForm.formModel, [field]: value },
                    })
                }
                onSubmitted={refetch}
                customerId={customerForm.customerId}
            />
            <DeleteCustomerForm
                open={deleteCustomerForm.open}
                onClose={() => setDeleteCustomerForm({ ...deleteCustomerForm, open: false })}
                customer={deleteCustomerForm.model}
                onSubmitted={refetch}
            />
        </AdminPanelPage>
    );
};

export default CustomersPage;
